<div class="container mx-auto px-4 py-12">
  <div class="max-w-2xl mx-auto">
    <h2 class="text-3xl font-bold mb-4 text-center">Direkt online buchen – verbindliche Bestellung</h2>
    <p class="text-center text-gray-700 mb-3">
      Mit dem Absenden lösen Sie eine verbindliche Buchung aus. Wir prüfen Verfügbarkeit und Logistik und bestätigen innerhalb von 24 Stunden.
    </p>
    <div class="bg-amber-50 border border-amber-200 text-amber-900 text-sm rounded-lg p-4 text-center mb-8">
      Buchung mit Verfügbarkeits- und Logistikprüfung. Transport- und Aufwandskosten werden anhand Ihrer Adresse berechnet und im Preisrechner ausgewiesen. Zahlung erfolgt per Rechnung nach Bestätigung.
    </div>
    
    <!-- Dev Mode Button -->
    <div *ngIf="isDevMode" class="mb-4 text-center">
      <button 
        type="button"
        (click)="fillWithRandomData()"
        class="bg-purple-600 hover:bg-purple-700 text-white px-6 py-2 rounded-lg font-semibold transition-all shadow-lg">
        🎲 Dev: Mit Zufallsdaten füllen
      </button>
    </div>
    
    <form [formGroup]="form" (ngSubmit)="onSubmit()" class="space-y-6 bg-white rounded-lg shadow-lg p-8">
      
      <!-- Booking Details -->
      <div class="border-b pb-6">
        <h3 class="text-lg font-semibold mb-4 text-brand-primary">Buchungsdaten – Zeitraum wählen</h3>
        
        <!-- Time Range Picker -->
        <div class="bg-gradient-to-br from-indigo-50 to-blue-50 rounded-lg p-6 border border-indigo-200 mb-6">
          <div class="grid grid-cols-1 md:grid-cols-3 gap-6 items-end">
            <!-- Start Date/Time -->
            <div>
              <div class="mb-4">
                <h4 class="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                  <span class="inline-flex items-center justify-center w-6 h-6 rounded-full bg-brand-primary text-white text-xs font-bold">1</span>
                  Startdatum & -zeit
                </h4>
                <div class="space-y-3">
                  <div class="form-group">
                    <input 
                      type="date" 
                      formControlName="startDate"
                      [min]="startMinDate"
                      (change)="onDateChange()"
                      placeholder=" "
                      [class.has-error]="form.get('startDate')?.touched && form.get('startDate')?.invalid">
                    <label>Datum *</label>
                    <span class="error-message" *ngIf="form.get('startDate')?.touched && form.get('startDate')?.invalid">
                      Datum erforderlich
                    </span>
                  </div>
                  <div class="form-group">
                    <input 
                      type="time" 
                      formControlName="startTime"
                      (change)="onDateChange()"
                      placeholder=" "
                      [class.has-error]="form.get('startTime')?.touched && form.get('startTime')?.invalid">
                    <label>Uhrzeit *</label>
                    <span class="error-message" *ngIf="form.get('startTime')?.touched && form.get('startTime')?.invalid">
                      Uhrzeit erforderlich
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Arrow -->
            <div class="hidden md:flex justify-center">
              <div class="flex flex-col items-center gap-2">
                <div class="text-2xl text-indigo-400">→</div>
                <span class="text-xs font-semibold text-gray-500 whitespace-nowrap">bis</span>
              </div>
            </div>

            <!-- End Date/Time -->
            <div>
              <div class="mb-4">
                <h4 class="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                  <span class="inline-flex items-center justify-center w-6 h-6 rounded-full bg-brand-accent text-white text-xs font-bold">2</span>
                  Enddatum & -zeit
                </h4>
                <div class="space-y-3">
                  <div class="form-group">
                    <input 
                      type="date" 
                      formControlName="endDate"
                      [min]="form.get('startDate')?.value || startMinDate"
                      (change)="onDateChange()"
                      placeholder=" "
                      [class.has-error]="form.get('endDate')?.touched && form.get('endDate')?.invalid">
                    <label>Datum *</label>
                    <span class="error-message" *ngIf="form.get('endDate')?.touched && form.get('endDate')?.invalid">
                      Datum erforderlich
                    </span>
                    <span class="error-message" *ngIf="form.touched && form.hasError('endBeforeStart')">
                      Nach Startdatum
                    </span>
                  </div>
                  <div class="form-group">
                    <input 
                      type="time" 
                      formControlName="endTime"
                      (change)="onDateChange()"
                      placeholder=" "
                      [class.has-error]="form.get('endTime')?.touched && form.get('endTime')?.invalid">
                    <label>Uhrzeit *</label>
                    <span class="error-message" *ngIf="form.get('endTime')?.touched && form.get('endTime')?.invalid">
                      Uhrzeit erforderlich
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Duration Display -->
          <div class="mt-6 pt-6 border-t border-indigo-200">
            <div class="flex items-center justify-between bg-white rounded-lg p-4">
              <div>
                <p class="text-sm text-gray-600">Mietdauer</p>
                <p class="text-2xl font-bold text-brand-primary">{{ durationDays }} Tag{{ durationDays !== 1 ? 'e' : '' }}</p>
              </div>

            </div>
          </div>
        </div>

          <!-- Delivery Address (before availability check) -->
      <div class="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <h4 class="text-sm font-semibold text-blue-900 mb-3">Lieferadresse (für Entfernungsberechnung)</h4>
        
        <!-- Autocomplete City Field -->
        <div class="form-group mb-4">
          <div class="relative">
            <input 
              type="text" 
              formControlName="address"
              (input)="onAddressInput($event)"
              (focus)="showAddressSuggestions = true"
              placeholder=" "
              autocomplete="off"
              [class.has-error]="form.get('address')?.touched && form.get('address')?.invalid">
            <label>Stadt (Autocomplete) *</label>
            
            <!-- Address Suggestions Dropdown -->
            <div *ngIf="showAddressSuggestions && addressSuggestions.length > 0" 
                 class="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
              <div *ngFor="let suggestion of addressSuggestions" 
                   (click)="selectAddress(suggestion)"
                   class="px-4 py-3 hover:bg-gray-100 cursor-pointer border-b last:border-b-0">
                <p class="text-sm">{{ suggestion.description }}</p>
              </div>
            </div>
          </div>
          <span class="error-message" *ngIf="form.get('address')?.touched && form.get('address')?.invalid">
            Stadt ist erforderlich
          </span>
          <!-- Distance Information -->
          <div *ngIf="calculatingDistance" class="helper-text text-blue-600">
            Entfernung wird berechnet...
          </div>
          <div *ngIf="!calculatingDistance && form.get('distance')?.value > 0" class="helper-text text-green-600">
            ✓ Einfache Lieferentfernung: {{ form.get('distance')?.value }}km
          </div>
        </div>
      </div>

        <!-- Helper Option with Toggle Switch -->
        <div class="mt-4 p-4 border border-gray-200 rounded-lg bg-blue-50">
          <div class="flex items-center justify-between">
            <div>
              <h3 class="text-lg font-semibold mb-4 text-brand-primary">Unterstützen und Kosten reduzieren?</h3>
              <p class="text-sm text-gray-600">Kunde unterstützt mit einem Helfer bei Auf- und Abbau.</p>
            </div>
            <button
              type="button"
              aria-label="Helfer wird gestellt"
              (click)="customerProvidesHelper = !customerProvidesHelper; calculatePrice()"
              [ngClass]="{
                'bg-brand-accent': customerProvidesHelper,
                'bg-gray-300': !customerProvidesHelper
              }"
              class="relative inline-flex h-8 w-14 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-brand-accent focus:ring-offset-2">
              <span
                [ngClass]="{
                  'translate-x-7': customerProvidesHelper,
                  'translate-x-1': !customerProvidesHelper
                }"
                class="inline-block h-6 w-6 transform rounded-full bg-white transition-transform">
              </span>
            </button>
          </div>
          <p class="text-xs text-gray-500 mt-2">
            {{ customerProvidesHelper ? 'Kundenseitig wird ein Helfer während des Auf- und Abbaus gestellt - wir kommen mit einer Person.' : 'Kundenseitig wird kein Helfer gestellt – wir kommen mit zwei Personen.' }}
          </p>
        </div>

        <!-- 360° Branding Option -->
        <div class="mt-4 p-4 border border-gray-200 rounded-lg bg-indigo-50">
          <div class="flex items-center justify-between">
            <div>
              <h3 class="text-lg font-semibold mb-2 text-brand-primary">360° Branding hinzufügen</h3>
              <p class="text-sm text-gray-600 mb-3">Komplette Branding der Videowand im Corporate Design (Front (unten), links, rechts, hinten))</p>
              <p class="text-xs text-gray-500 italic">
                Optional. Voraussetzung: Buchung mindestens 14 Tage im Voraus. Express-Buchungen nur auf persönliche Absprache.
                Grafikvorlagen stellen wir bereit; die Grafikerstellung übernehmen Sie.
                Der Preis wird im individuellen Angebot bestätigt.
              </p>
            </div>
            <button
              type="button"
              aria-label="360° Branding hinzufügen"
              (click)="includes360Branding = !includes360Branding; calculatePrice()"
              [ngClass]="{
                'bg-brand-accent': includes360Branding,
                'bg-gray-300': !includes360Branding
              }"
              class="relative inline-flex h-8 w-14 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-brand-accent focus:ring-offset-2 flex-shrink-0">
              <span
                [ngClass]="{
                  'translate-x-7': includes360Branding,
                  'translate-x-1': !includes360Branding
                }"
                class="inline-block h-6 w-6 transform rounded-full bg-white transition-transform">
              </span>
            </button>
          </div>
        </div>
      </div>

    

      <!-- Availability Check (Hidden - Auto Triggered) -->
      <div *ngIf="priceBreakdown" class="border-b pb-6">
        <h3 class="text-lg font-semibold mb-4 text-brand-primary">Verfügbarkeitsprüfung</h3>
        
        <div class="space-y-4">
          <!-- Availability Result -->
          <div *ngIf="availabilityChecked" class="p-4 rounded-lg" [ngClass]="{
            'bg-green-50 border border-green-200': availabilityStatus === 'available',
            'bg-red-50 border border-red-200': availabilityStatus === 'unavailable',
            'bg-yellow-50 border border-yellow-200': availabilityStatus === 'partial'
          }">
            <div class="flex items-center gap-3">
              <div *ngIf="availabilityStatus === 'available'" class="text-2xl">✓</div>
              <div *ngIf="availabilityStatus === 'unavailable'" class="text-2xl">✗</div>
              <div *ngIf="availabilityStatus === 'partial'" class="text-2xl">⚠</div>
              <div>
                <p class="font-semibold" [ngClass]="{
                  'text-green-800': availabilityStatus === 'available',
                  'text-red-800': availabilityStatus === 'unavailable',
                  'text-yellow-800': availabilityStatus === 'partial'
                }">
                  {{ availabilityMessage }}
                </p>
                <p class="text-sm text-gray-600 mt-1">{{ availabilityDetails }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Price Summary with Breakdown -->
      <div *ngIf="priceBreakdown">
        <div class="flex items-center justify-between mb-2">
          <h3 class="text-lg font-semibold text-brand-primary">Preisrechner – verbindliche Bestellung</h3>
          <span class="text-xs text-gray-500">Netto &amp; Brutto werden ausgewiesen</span>
        </div>
        <div class="bg-gradient-to-r from-brand-primary to-brand-accent text-white p-6 rounded-lg">
          <div class="flex justify-between items-center mb-2">
            <span class="text-lg">Gesamtpreis (netto):</span>
            <span class="text-3xl font-bold">€{{ calculatedPrice | number:'1.2-2' }}</span>
          </div>

        
          <!-- Price Breakdown -->
          <div *ngIf="priceBreakdown" class="mt-4 pt-4 border-t border-white border-opacity-30 space-y-2 text-sm">
            <div class="flex justify-between">
              <span>Lieferung & Setup (inkl. Auf-/Abbau{{ priceBreakdown.customerProvidesHelper ? ' mit Helfer' : '' }}):</span>
              <span>€{{ priceBreakdown.deliverySetupCost | number:'1.2-2' }}</span>
            </div>
            <div class="flex justify-between">
              <span>
                Mietkosten
                <span *ngIf="priceBreakdown.durationDays > 1">
                  ({{ (priceBreakdown.rentalCost / priceBreakdown.durationDays) | number:'1.2-2' }} € pro Tag)
                </span>
                :
              </span>
              <span>€{{ priceBreakdown.rentalCost | number:'1.2-2' }}</span>
            </div>
            <div *ngIf="includes360Branding && priceBreakdown.brandingCost && priceBreakdown.brandingCost > 0" class="flex justify-between text-amber-100">
              <span>360° Branding:</span>
              <span>€{{ priceBreakdown.brandingCost | number:'1.2-2' }}</span>
            </div>
            <div class="flex justify-between pt-3 border-t border-white border-opacity-30">
              <span>Zwischensumme (netto):</span>
              <span>€{{ priceBreakdown.totalPrice | number:'1.2-2' }}</span>
            </div>
            <div class="flex justify-between">
              <span>zzgl. 19% MwSt.:</span>
              <span>€{{ (priceBreakdown.totalPrice * 0.19) | number:'1.2-2' }}</span>
            </div>
            <div class="flex justify-between font-semibold">
              <span>Gesamtpreis (brutto):</span>
              <span>€{{ (priceBreakdown.totalPrice * 1.19) | number:'1.2-2' }}</span>
            </div>
          </div>

          <!-- DEV MODE: Detailed Calculation Breakdown 
          <div *ngIf="showDevMode && priceBreakdownDetails && priceBreakdown" class="mt-6 p-4 bg-gray-900 text-gray-100 rounded-lg border border-gray-700 font-mono text-xs">
            <div class="mb-3 pb-3 border-b border-gray-700">
              <p class="text-yellow-400 font-bold">🔧 DEV MODE - Kalkulation Lieferung & Setup</p>
            </div>
            
            <div class="space-y-2 text-gray-300">
              <p><span class="text-blue-400">Entfernung (einfache Fahrt):</span> {{ priceBreakdownDetails.distance }} km</p>
              <p><span class="text-blue-400">Fahrtdauer (eine Richtung):</span> {{ priceBreakdownDetails.durationSeconds }} sec = {{ (priceBreakdownDetails.durationSeconds / 60) | number:'1.1-1' }} Min</p>
              <p><span class="text-blue-400">Fahrtdauer (insgesamt 4x):</span> {{ priceBreakdownDetails.durationSeconds * 4 }} sec = {{ ((priceBreakdownDetails.durationSeconds * 4) / 3600) | number:'1.2-2' }} Std</p>
              <p><span class="text-blue-400">Personen:</span> {{ priceBreakdownDetails.personCount }} ({{ priceBreakdownDetails.customerProvidesHelper ? 'Kunde hilft mit' : 'Ohne Hilfe' }})</p>
              
              <div class="mt-3 pt-3 border-t border-gray-700 space-y-2">
                <p class="text-green-400">📦 Lieferkosten: {{ priceBreakdownDetails.distance }} × 4 × €0,30 = <strong>€{{ priceBreakdownDetails.deliveryKmCost | number:'1.2-2' }}</strong></p>
                
                <p class="text-green-400">🚗 Fahrtkosten: ({{ priceBreakdownDetails.durationSeconds }} × 4) / 3600 × €40 × {{ priceBreakdownDetails.personCount }} = {{ ((priceBreakdownDetails.durationSeconds * 4) / 3600) | number:'1.2-2' }}h × €40 × {{ priceBreakdownDetails.personCount }} = <strong>€{{ priceBreakdownDetails.drivingCost | number:'1.2-2' }}</strong></p>
                
                <p class="text-green-400">🔧 Auf-/Abbau: 1,75h × {{ priceBreakdownDetails.personCount }} × €40 = <strong>€{{ priceBreakdownDetails.setupTeardownCost | number:'1.2-2' }}</strong></p>
                
                <div class="mt-2 pt-2 border-t border-gray-600">
                  <p class="text-white">Summe: €{{ priceBreakdownDetails.deliveryKmCost | number:'1.2-2' }} + €{{ priceBreakdownDetails.drivingCost | number:'1.2-2' }} + €{{ priceBreakdownDetails.setupTeardownCost | number:'1.2-2' }} = <strong class="text-yellow-300">€{{ (priceBreakdownDetails.deliveryKmCost + priceBreakdownDetails.drivingCost + priceBreakdownDetails.setupTeardownCost) | number:'1.2-2' }}</strong></p>
                  <p class="text-white mt-1">Mindespreis: €{{ priceBreakdownDetails.minSetupPrice }}</p>
                  <p class="text-yellow-400 font-bold mt-2">✓ FINAL: <strong>€{{ priceBreakdown.deliverySetupCost | number:'1.2-2' }}</strong></p>
                </div>
              </div>
            </div>
          </div>-->

        </div>
        <p class="text-xs text-gray-600 mt-3">Alle Preise in EUR. Transport- und Auf-/Abbaukosten werden transparent angezeigt; keine versteckten Gebühren.</p>
      </div>

      <!-- After Booking Steps -->
      <div *ngIf="priceBreakdown" class="border rounded-lg border-gray-200 bg-gray-50 p-4 space-y-2">
        <h4 class="text-base font-semibold text-gray-800">Was passiert nach dem Klick?</h4>
        <div class="grid grid-cols-1 gap-2 text-sm text-gray-700">
          <div class="flex items-start gap-2">
            <span class="font-semibold text-brand-primary">1.</span>
            <span>Wir prüfen Termin und Logistik innerhalb von einem Werktag.</span>
          </div>
          <div class="flex items-start gap-2">
            <span class="font-semibold text-brand-primary">2.</span>
            <span>Wir bestätigen Ihre Buchung nach positiver Prüfung.</span>
          </div>
          <div class="flex items-start gap-2">
            <span class="font-semibold text-brand-primary">3.</span>
            <span>Wir vereinbaren Lieferung, Aufbau, Einweisung und Abholung zum vereinbarten Termin.</span>
          </div>
        </div>
      </div>

      <!-- Customer Information -->
      <div class="border-b pb-6" *ngIf="priceBreakdown">
        <h3 class="text-lg font-semibold mb-4 text-brand-primary">Kontaktinformationen</h3>
        
        <!-- Row 1: Name & Company -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div class="form-group">
            <input 
              type="text" 
              formControlName="firstName"
              placeholder=" "
              [class.has-error]="form.get('firstName')?.touched && form.get('firstName')?.invalid">
            <label>Vorname *</label>
            <span class="error-message" *ngIf="form.get('firstName')?.touched && form.get('firstName')?.invalid">
              Vorname erforderlich (min. 2 Zeichen)
            </span>
          </div>

          <div class="form-group">
            <input 
              type="text" 
              formControlName="lastName"
              placeholder=" "
              [class.has-error]="form.get('lastName')?.touched && form.get('lastName')?.invalid">
            <label>Nachname *</label>
            <span class="error-message" *ngIf="form.get('lastName')?.touched && form.get('lastName')?.invalid">
              Nachname erforderlich (min. 2 Zeichen)
            </span>
          </div>
        </div>

        <!-- Row 2: Company -->
        <div class="mb-4">
          <div class="form-group">
            <input 
              type="text" 
              formControlName="companyName"
              placeholder=" ">
            <label>Firma (optional)</label>
          </div>
        </div>

        <!-- Row 3: Billing Address Section -->
        <div class="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <h4 class="text-sm font-semibold text-amber-900 mb-3">Lieferadresse (Detailadresse)</h4>
          
          <!-- Street & House Number -->
          <div class="form-group mb-4">
            <input 
              type="text" 
              formControlName="street"
              placeholder=" "
              [class.has-error]="form.get('street')?.touched && form.get('street')?.invalid">
            <label>Straße & Hausnummer *</label>
            <span class="error-message" *ngIf="form.get('street')?.touched && form.get('street')?.invalid">
              Straße ist erforderlich
            </span>
          </div>

          <!-- Postal Code & City (structured) -->
          <div class="grid grid-cols-2 gap-4">
            <div class="form-group">
              <input 
                type="text" 
                formControlName="postalCode"
                placeholder=" "
                maxlength="5"
                [class.has-error]="form.get('postalCode')?.touched && form.get('postalCode')?.invalid">
              <label>PLZ *</label>
              <span class="error-message" *ngIf="form.get('postalCode')?.touched && form.get('postalCode')?.invalid">
                5-stellige PLZ erforderlich
              </span>
            </div>

            <div class="form-group">
              <input 
                type="text" 
                formControlName="city"
                placeholder=" "
                [class.has-error]="form.get('city')?.touched && form.get('city')?.invalid">
              <label>Stadt *</label>
              <span class="error-message" *ngIf="form.get('city')?.touched && form.get('city')?.invalid">
                Stadt erforderlich
              </span>
            </div>
          </div>
        </div>

        <!-- Row 3: Billing Address (Different from Delivery) -->
        <div class="mb-6">
          

          <div *ngIf="showBillingAddress" class="p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
            <h4 class="text-sm font-semibold text-indigo-900 mb-3">Rechnungsadresse</h4>
            
            <div class="form-group mb-4">
              <input 
                type="text" 
                formControlName="billingStreet"
                placeholder=" ">
              <label>Straße & Hausnummer</label>
            </div>

            <div class="grid grid-cols-2 gap-4">
              <div class="form-group">
                <input 
                  type="text" 
                  formControlName="billingPostalCode"
                  placeholder=" "
                  maxlength="5">
                <label>PLZ</label>
              </div>

              <div class="form-group">
                <input 
                  type="text" 
                  formControlName="billingCity"
                  placeholder=" ">
                <label>Stadt</label>
              </div>
            </div>
          </div>
        </div>

        <!-- Row 4: Email & Phone -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div class="form-group">
            <input 
              type="email" 
              formControlName="email"
              placeholder=" "
              [class.has-error]="form.get('email')?.touched && form.get('email')?.invalid">
            <label>E-Mail *</label>
            <span class="error-message" *ngIf="form.get('email')?.touched && form.get('email')?.invalid">
              Gültige E-Mail erforderlich
            </span>
          </div>

          <div class="form-group">
            <input 
              type="tel" 
              formControlName="phone"
              placeholder=" "
              [class.has-error]="form.get('phone')?.touched && form.get('phone')?.invalid">
            <label>Telefon *</label>
            <span class="error-message" *ngIf="form.get('phone')?.touched && form.get('phone')?.invalid">
              Gültige Telefonnummer erforderlich
            </span>
          </div>
        </div>

        <!-- Notes & Special Requests -->
        <div class="form-group mb-4">
          <textarea 
            formControlName="notes"
            placeholder=" "
            rows="4"></textarea>
          <label>Notizen & Wünsche (optional)</label>
          <span class="helper-text">Spezielle Anforderungen, Zufahrtsanweisungen, Aufbauort, besondere Wünsche...</span>
        </div>

        <!-- Branding Notes (shown when Branding is selected) -->
        <div *ngIf="includes360Branding" class="form-group mb-4">
          <textarea 
            formControlName="brandingNotes"
            placeholder=" "
            rows="3"></textarea>
          <label>Grafik-Spezifikationen & Anmerkungen zum Branding</label>
          <span class="helper-text">Geben Sie hier spezielle Anforderungen zum 360° Branding an (z.B. Farben, Logos, besondere Wünsche)</span>
        </div>
      </div>

      <!-- Terms & Conditions Toggles -->
      <div *ngIf="priceBreakdown" class="space-y-3 mb-6">
        <!-- AGB & Datenschutz (immer erforderlich) -->
        <div class="flex items-center justify-between p-4 bg-gray-50 border rounded-lg" [class.border-red-300]="form.get('acceptedTerms')?.touched && form.get('acceptedTerms')?.invalid">
          <label for="acceptedTerms" class="text-sm text-gray-700 cursor-pointer flex-1 pr-4">
            Ich akzeptiere die <a [href]="apiUrl + '/agb.pdf'" target="_blank" class="text-brand-primary font-semibold underline hover:text-brand-accent">AGB</a> und die <a [href]="apiUrl + '/datenschutz.pdf'" target="_blank" class="text-brand-primary font-semibold underline hover:text-brand-accent">Datenschutzerklärung</a> *
          </label>
          <label class="relative inline-flex items-center cursor-pointer">
            <input 
              type="checkbox" 
              id="acceptedTerms"
              formControlName="acceptedTerms"
              class="sr-only peer">
            <div class="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-brand-accent/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-brand-accent"></div>
          </label>
        </div>

        <!-- Widerrufsbelehrung (nur für Verbraucher/B2C) -->
        <div *ngIf="isB2C" class="flex items-center justify-between p-4 bg-blue-50 border border-blue-200 rounded-lg" [class.border-red-300]="form.get('acceptedRevocation')?.touched && form.get('acceptedRevocation')?.invalid">
          <label for="acceptedRevocation" class="text-sm text-gray-700 cursor-pointer flex-1 pr-4">
            Ich habe die <a [href]="apiUrl + '/widerrufsbelehrung.pdf'" target="_blank" class="text-brand-primary font-semibold underline hover:text-brand-accent">Widerrufsbelehrung</a> zur Kenntnis genommen *
          </label>
          <label class="relative inline-flex items-center cursor-pointer">
            <input 
              type="checkbox" 
              id="acceptedRevocation"
              formControlName="acceptedRevocation"
              class="sr-only peer">
            <div class="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-brand-accent/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-brand-accent"></div>
          </label>
        </div>
      </div>

      <!-- Submit Button -->
      <button  *ngIf="priceBreakdown"
        type="submit"
        [disabled]="!form.valid || loading || durationDays <= 0 || (isB2C && !form.get('acceptedRevocation')?.value)"
        class="w-full bg-brand-accent text-white font-bold py-3 rounded-lg hover:opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed">
        {{ loading ? 'Buchung wird erstellt...' : 'Jetzt verbindlich anfragen & buchen' }}
      </button>

      <p class="text-sm text-gray-700 text-center" *ngIf="priceBreakdown">
        Verbindlich nach Bestätigung – keine Online-Zahlung erforderlich
      </p>
      <p class="text-xs text-gray-600 text-center" *ngIf="priceBreakdown">Keine versteckten Kosten · Persönlicher Ansprechpartner · Klare Stornobedingungen · Rechnung per E-Mail</p>
    </form>
  </div>
</div>
